/* eslint-disable ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
import { get, set, action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { filterValue } from '@linc-technologies/linced-common/utils/filtering';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';

export default class NewConnection extends Component {
  // Services
  @service
  store;

  @service
  toaster;

  // props
  heroSchools = [];

  refresh() {}

  // variables
  @tracked
  dialogueOpen = false;

  @tracked
  heroSchool = null;

  @tracked
  heroSchoolFilters = [];

  @tracked
  isLoading = false;

  @tracked
  postcode = null;

  @action
  setPostcode(value) {
    this.postcode = value;
  }

  @tracked
  step = 'heroSchool';

  @tracked
  wondeSchool = null;

  @tracked
  wondeSchools = [];

  @tracked
  wondeSchoolFilters = [];

  @computed('heroSchools.[]', 'heroSchoolFilters.@each.{field,value}')
  get filteredHeroSchools() {
    if (isEmpty(this.heroSchoolFilters)) {
      // No filters - just return the data.
      return this.heroSchools;
    }

    // Determine datatypes for any specified filters.
    let filters = this.heroSchoolFilters.map((filter) => {
      return {
        key: filter.field,
        dataType: DataType.STRING,
        value: filter.value,
      };
    });

    return this.heroSchools.filter((row) => {
      return filters.every((filter) => {
        // Generic filter processing.
        return filterValue(
          get(row, filter.key) ?? '',
          filter.dataType,
          filter.value,
        );
      });
    });
  }

  @computed('wondeSchools.[]', 'wondeSchoolFilters.@each.{field,value}')
  get filteredWondeSchools() {
    if (isEmpty(this.wondeSchoolFilters)) {
      // No filters - just return the data.
      return this.wondeSchools;
    }

    // Determine datatypes for any specified filters.
    let filters = this.wondeSchoolFilters.map((filter) => {
      return {
        key: filter.field,
        dataType: DataType.STRING,
        value: filter.value,
      };
    });

    return this.wondeSchools.filter((row) => {
      return filters.every((filter) => {
        // Generic filter processing.
        return filterValue(
          get(row, filter.key) ?? '',
          filter.dataType,
          filter.value,
        );
      });
    });
  }

  @action
  createConnection() {
    this.store
      .createRecord('wonde/connection', {
        schoolId: this.heroSchool.id,
        wondeId: this.wondeSchool.id,
      })
      .save()
      .then(() => {
        this.toaster.success('Connection created successfully');
      })
      .catch((err) => {
        this.toaster.error(`Error creating connection: ${err}`);
      })
      .finally(() => {
        this.toggleDialogue();
        this.refresh();
      });
  }

  @action
  filterHeroSchools(field, value) {
    let filter = findBy(this.heroSchoolFilters, 'field', field);
    if (isEmpty(filter)) {
      // No existing filter on this field; add it.
      this.heroSchoolFilters = [...this.heroSchoolFilters, { field, value }];
      return;
    }

    if (isEmpty(value)) {
      // Filter has been cleared; remove it.
      this.heroSchoolFilters = this.heroSchoolFilters.filter(
        (f) => f !== filter,
      );
    } else {
      // Existing filter has been changed; update it.
      set(filter, 'value', value);
    }
  }

  @action
  filterWondeSchools(field, value) {
    let filter = findBy(this.wondeSchoolFilters, 'field', field);
    if (isEmpty(filter)) {
      // No existing filter on this field; add it.
      this.wondeSchoolFilters = [...this.wondeSchoolFilters, { field, value }];
      return;
    }

    if (isEmpty(value)) {
      // Filter has been cleared; remove it.
      this.wondeSchoolFilters = this.wondeSchoolFilters.filter(
        (f) => f !== filter,
      );
    } else {
      // Existing filter has been changed; update it.
      set(filter, 'value', value);
    }
  }

  @action
  goBack() {
    if (this.step === 'wondeSchool') {
      this.step = 'heroSchool';
      this.heroSchool = null;
      return;
    }
    this.step = 'wondeSchool';
    this.wondeSchool = null;
  }

  @action
  selectHeroSchool(school) {
    this.step = 'wondeSchool';

    this.postcode = school.physicalAddress
      ? school.physicalAddress.postcode || ''
      : '';

    this.heroSchool = school;
  }

  @action
  selectWondeSchool(school) {
    this.step = 'confirmation';
    this.wondeSchool = school;
  }

  @action
  searchSchools() {
    let params = {
      postcode: this.postcode,
    };
    this.isLoading = true;
    this.store.query('wonde/school', params).then((res) => {
      this.isLoading = false;
      this.wondeSchools = res;
      this.postcode = '';
    });
  }

  @action
  toggleDialogue() {
    this.step = 'heroSchool';
    this.dialogueOpen = !this.dialogueOpen;
    this.heroSchool = null;
    this.wondeSchool = null;
    this.wondeSchools = [];
  }
}
